import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isAdmin as isAdminRole } from "@prism/shared/constants";
import { Card, CardHeader, CardBody, DataTable } from "../components/index.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useActiveClient } from "../hooks/useActiveClient.js";
import { useSyncStatusContext } from "../context/SyncStatusContext.js";
import { useApiAction } from "../hooks/useApi.js";
import { timeAgo } from "../lib/time.js";

const STATUS_COLORS = {
  connected: "bg-positive",
  error: "bg-negative",
  disconnected: "bg-text-muted",
  pending: "bg-text-muted",
};

const SOURCE_LABELS = {
  ga4: "Google Analytics 4",
  google_ads: "Google Ads",
  shopify: "Shopify",
  meta_ads: "Meta Ads",
  hubspot: "HubSpot",
  mailchimp: "Mailchimp",
  klaviyo: "Klaviyo",
  callrail: "CallRail",
};

export function Account() {
  const { user } = useAuth();
  // Business info must reflect the *effective* client (the admin's view-as
  // target), not the static home client from the session — same hook the
  // sidebar/theme/dashboard use. useAuth still owns the logged-in user.
  const { client } = useActiveClient();
  const syncStatus = useSyncStatusContext();
  const isAdmin = isAdminRole(user?.role);
  // The viewed client lives in the URL as ?clientId (set by the ClientSwitcher).
  // Action POSTs below must carry it so they target the *viewed* client, not the
  // admin's home client from the JWT — the backend only honors the view-as
  // override when the request includes ?clientId (see middleware/auth.js).
  const [searchParams] = useSearchParams();
  const viewClientId = searchParams.get("clientId");

  // Live sync status only (type, status, lastSync) — no mock merge.
  const sources = syncStatus.sources || [];

  const users = user
    ? [{ name: user.name, email: user.email, role: user.role, lastLogin: "This session" }]
    : [];

  return (
    <div className="space-y-5 max-w-[1000px]">
      <Card>
        <CardHeader
          title="Business"
          subtitle="Public-facing identity"
        />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Business Name" value={client?.name} />
            <Field label="Website"       value={client?.website} />
            <Field label="Industry"      value={client?.industry} />
            <Field label="Location"      value={client?.location} />
            <Field
              label="Accent Color"
              value={
                <span className="inline-flex items-center gap-2">
                  <span
                    className="inline-block w-5 h-5 rounded border border-border-default"
                    style={{ backgroundColor: client?.accentColor || "#ff6804" }}
                  />
                  <span className="font-mono text-[12px]">{client?.accentColor}</span>
                </span>
              }
            />
            <Field label="Plan" value={titleCase(client?.plan)} />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Data Sources"
          subtitle="Connected platforms · managed by BMG"
          right={
            isAdmin && (
              <SyncActions syncStatus={syncStatus} clientId={viewClientId} />
            )
          }
        />
        <CardBody className="pt-0 pb-2">
          <div className="divide-y divide-border-light -mx-5">
            {sources.length === 0 && (
              <div className="px-5 py-4 text-[12px] text-text-muted">
                No data sources connected yet.
              </div>
            )}
            {sources.map((ds) => (
              <div key={ds.type} className="flex items-center gap-4 px-5 py-4">
                <div className="flex items-center gap-2 w-56 flex-shrink-0">
                  <span
                    className={`w-2 h-2 rounded-full ${STATUS_COLORS[ds.status] || STATUS_COLORS.disconnected}`}
                    aria-label={ds.status}
                  />
                  <span className="text-[13px] font-medium text-text-primary">
                    {SOURCE_LABELS[ds.type] || ds.type}
                  </span>
                </div>
                <div className="flex-1" />
                <div className="text-[11px] text-text-muted">
                  Last sync · {ds.lastSync ? timeAgo(ds.lastSync) : "never"}
                </div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {isAdmin && <InsightActions clientId={viewClientId} />}

      {isAdmin && (
        <Card>
          <CardHeader
            title="BMG Admin"
            subtitle="Manage all clients on the platform"
            right={
              <Link
                to="/admin/clients"
                className="h-8 px-3 rounded border border-border-default text-[12px] text-text-secondary hover:bg-[var(--color-border-light)] transition-colors inline-flex items-center"
              >
                Manage clients →
              </Link>
            }
          />
        </Card>
      )}

      <Card>
        <CardHeader title="Users" subtitle="Who has access" />
        <DataTable
          columns={[
            { key: "name",      label: "Name" },
            { key: "email",     label: "Email" },
            { key: "role",      label: "Role" },
            { key: "lastLogin", label: "Last Login", align: "right" },
          ]}
          rows={users}
        />
      </Card>

      {/* Billing card intentionally absent — billing ships in a later phase.
          Never render placeholder invoices/payment methods to real clients. */}
    </div>
  );
}

/**
 * Sync controls — admin-only. Inline feedback below each button.
 * "Seed demo" exists because production clients without a Windsor account
 * need a way to see the dashboard with realistic data during onboarding.
 */
function SyncActions({ syncStatus, clientId }) {
  const api = useApiAction();
  const [windsor, setWindsor] = useState({ busy: false, msg: null, error: null });
  const [demo,    setDemo]    = useState({ busy: false, msg: null, error: null });

  async function runWindsor() {
    setWindsor({ busy: true, msg: null, error: null });
    try {
      const r = await api(withClientId("/api/sync/run", clientId), { method: "POST" });
      const summary = Object.entries(r.sources || {})
        .map(([k, v]) => (v.error ? `${k}: ${v.error}` : v.skipped ? `${k}: skipped (${v.reason})` : `${k}: ${v.records} days`))
        .join(" · ");
      setWindsor({ busy: false, msg: summary || "Sync complete.", error: null });
      syncStatus.refetch?.();
    } catch (e) {
      setWindsor({ busy: false, msg: null, error: e.message });
    }
  }

  async function runDemo() {
    setDemo({ busy: true, msg: null, error: null });
    try {
      const r = await api(withClientId("/api/sync/demo", clientId), { method: "POST" });
      setDemo({ busy: false, msg: `Seeded ${r.seeded} rows over ${r.rangeDays} days`, error: null });
      syncStatus.refetch?.();
    } catch (e) {
      setDemo({ busy: false, msg: null, error: e.message });
    }
  }

  return (
    <div className="flex items-start gap-2 text-right">
      <ActionButton label="Sync Windsor" state={windsor} onClick={runWindsor} />
      {/* "Seed demo" is hidden for every client but the sanctioned demo one.
          This is presentation, NOT enforcement: POST /api/sync/demo refuses the
          same cases server-side, and that refusal is the authority. The button
          used to sit here for every client, one ghost-styled click away from
          "Sync Windsor", writing synthetic Google Ads spend that billing cannot
          distinguish from live spend. */}
      {syncStatus.demoSeedingAllowed && (
        <ActionButton label="Seed demo" state={demo} onClick={runDemo} variant="ghost" />
      )}
    </div>
  );
}

function InsightActions({ clientId }) {
  const api = useApiAction();
  const [state, setState] = useState({ busy: false, msg: null, error: null });

  async function run() {
    setState({ busy: true, msg: null, error: null });
    try {
      const r = await api(withClientId("/api/insights/generate", clientId), {
        method: "POST",
        body: JSON.stringify({}),
      });
      const line = `${r.created + r.updated} insights · ${r.tokens.input + r.tokens.output} tokens`;
      setState({ busy: false, msg: line, error: null });
    } catch (e) {
      setState({ busy: false, msg: null, error: e.message });
    }
  }

  return (
    <Card>
      <CardHeader
        title="AI Insights"
        subtitle="Generate narrative insights from the latest data"
        right={<ActionButton label="Regenerate insights" state={state} onClick={run} />}
      />
      {(state.msg || state.error) && (
        <CardBody className="pt-0">
          <div className={`text-[12px] ${state.error ? "text-negative" : "text-text-secondary"}`}>
            {state.error || state.msg}
          </div>
        </CardBody>
      )}
    </Card>
  );
}

/**
 * Append the view-as client id as a query param so an admin's action targets
 * the client they're currently viewing rather than their JWT home client.
 * Returns the path unchanged for the home client (no ?clientId in the URL).
 */
function withClientId(path, clientId) {
  return clientId ? `${path}?clientId=${encodeURIComponent(clientId)}` : path;
}

function ActionButton({ label, state, onClick, variant = "solid" }) {
  const base = "h-8 px-3 rounded text-[12px] font-medium transition-colors disabled:opacity-60 disabled:cursor-wait";
  const styles = variant === "solid"
    ? "bg-accent text-white hover:opacity-90"
    : "border border-border-default text-text-secondary hover:bg-[var(--color-border-light)]";
  return (
    <div className="flex flex-col items-end gap-1">
      <button type="button" onClick={onClick} disabled={state.busy} className={`${base} ${styles}`}>
        {state.busy ? "Working…" : label}
      </button>
      {state.msg && (
        <span className="text-[10px] text-text-muted max-w-[240px] truncate" title={state.msg}>
          {state.msg}
        </span>
      )}
      {state.error && (
        <span className="text-[10px] text-negative max-w-[240px] truncate" title={state.error}>
          {state.error}
        </span>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase text-text-muted font-medium mb-1"
        style={{ letterSpacing: "0.5px" }}
      >
        {label}
      </div>
      <div className="text-[13px] text-text-primary">{value || "—"}</div>
    </div>
  );
}

function titleCase(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
